import { useQuery } from '@tanstack/react-query';
import { whatsappService, facebookService } from '@/services/social-channels.service';

/** The social channels that have their own page in the nav rail. */
export type RailSocialChannel = 'whatsapp' | 'facebook';

export type LiveSocialChannels = Record<RailSocialChannel, boolean>;

/** Settings → Social Channels invalidates this after a save or delete. */
export const LIVE_SOCIAL_CHANNELS_KEY = ['channels', 'live-social'] as const;

const NONE: LiveSocialChannels = { whatsapp: false, facebook: false };

/**
 * A channel is live once it has been configured AND switched on. A saved but
 * disabled config ("Configured" in Settings) does not count.
 */
export function isChannelLive(config: { isActive?: boolean; enabled?: boolean } | null | undefined): boolean {
  if (!config) return false;
  return (config.isActive ?? config.enabled) === true;
}

/**
 * Which social channels the nav rail should offer. Every signed-in role may
 * read the configs (secrets come back masked), so this works for agents too.
 * Until the answer arrives, and if it fails, nothing is shown: an entry that
 * leads to an unconfigured inbox is the thing this exists to prevent.
 */
export function useLiveSocialChannels(): LiveSocialChannels {
  const { data } = useQuery({
    queryKey: LIVE_SOCIAL_CHANNELS_KEY,
    queryFn: async (): Promise<LiveSocialChannels> => {
      const [whatsapp, facebook] = await Promise.all([
        whatsappService.getConfig(),
        facebookService.getConfig(),
      ]);
      return { whatsapp: isChannelLive(whatsapp), facebook: isChannelLive(facebook) };
    },
    staleTime: 5 * 60_000,
  });
  return data ?? NONE;
}
